import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import {
  LayoutDashboard,
  Calendar,
  Scissors,
  ShoppingBag,
  Package,
  FileText,
  MessageSquare,
  Star,
  Image,
  Users,
  Settings,
  LogOut,
  Menu,
  X,
  ChevronDown,
  Percent
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';

const navItems = [
  { name: 'Dashboard', path: '/admin', icon: LayoutDashboard },
  { name: 'Rendez-vous', path: '/admin/bookings', icon: Calendar },
  { name: 'Calendrier', path: '/admin/calendar', icon: Calendar },
  { name: 'Services', path: '/admin/services', icon: Scissors },
  { name: 'Réductions', path: '/admin/discounts', icon: Percent },
  { name: 'Produits', path: '/admin/products', icon: ShoppingBag },
  { name: 'Commandes', path: '/admin/orders', icon: Package },
  { name: 'Blog', path: '/admin/blog', icon: FileText },
  { name: 'Messages', path: '/admin/messages', icon: MessageSquare },
  { name: 'Témoignages', path: '/admin/testimonials', icon: Star },
  { name: 'Galerie', path: '/admin/gallery', icon: Image },
  { name: 'Utilisateurs', path: '/admin/users', icon: Users, adminOnly: true },
];

export const AdminLayout = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = React.useState(false);
  const { user, logout, isAdmin } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate('/admin/login');
  };

  const filteredNavItems = navItems.filter(item => !item.adminOnly || isAdmin);

  return (
    <div className="min-h-screen bg-[#F5F0EB]">
      {/* Mobile Header */}
      <header className="lg:hidden bg-[#2C1810] text-white p-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#C9A96E] to-[#A67B5B] flex items-center justify-center">
            <span className="text-white font-bold font-serif">MB</span>
          </div>
          <span className="font-serif font-semibold">Miss Baleng Admin</span>
        </div>
        <Button
          variant="ghost"
          size="icon"
          onClick={() => setSidebarOpen(!sidebarOpen)}
          className="text-white hover:bg-[#3E2723]"
        >
          {sidebarOpen ? <X size={24} /> : <Menu size={24} />}
        </Button>
      </header>

      <div className="flex">
        {/* Sidebar */}
        <aside
          className={`fixed inset-y-0 left-0 z-50 w-64 bg-[#2C1810] text-white transform transition-transform duration-300 lg:relative lg:translate-x-0 ${
            sidebarOpen ? 'translate-x-0' : '-translate-x-full'
          }`}
        >
          {/* Logo */}
          <div className="p-6 border-b border-[#3E2723] hidden lg:block">
            <Link to="/admin" className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#C9A96E] to-[#A67B5B] flex items-center justify-center">
                <span className="text-white font-bold text-xl font-serif">MB</span>
              </div>
              <div>
                <h1 className="font-serif font-bold text-lg">Miss Baleng</h1>
                <p className="text-xs text-[#C9A96E]">Administration</p>
              </div>
            </Link>
          </div>

          {/* Navigation */}
          <nav className="p-4 space-y-1 overflow-y-auto h-[calc(100vh-200px)]">
            {filteredNavItems.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => setSidebarOpen(false)}
                  className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                    isActive
                      ? 'bg-[#C9A96E] text-white'
                      : 'text-[#D4C4B5] hover:bg-[#3E2723] hover:text-white'
                  }`}
                >
                  <Icon size={20} />
                  <span>{item.name}</span>
                </Link>
              );
            })}
          </nav>

          {/* User Menu */}
          <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-[#3E2723]">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="w-full flex items-center gap-3 px-4 py-3 rounded-lg hover:bg-[#3E2723] transition-colors">
                  <div className="w-10 h-10 rounded-full bg-[#C9A96E] flex items-center justify-center">
                    <span className="font-semibold">{user?.firstName?.[0]}{user?.lastName?.[0]}</span>
                  </div>
                  <div className="flex-1 text-left">
                    <p className="text-sm font-medium text-white">{user?.firstName} {user?.lastName}</p>
                    <p className="text-xs text-[#A67B5B] capitalize">{user?.role}</p>
                  </div>
                  <ChevronDown size={16} className="text-[#A67B5B]" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuItem asChild>
                  <Link to="/" className="cursor-pointer">
                    Voir le site
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout} className="text-red-600 cursor-pointer">
                  <LogOut className="mr-2" size={16} />
                  Déconnexion
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </aside>

        {/* Overlay */}
        {sidebarOpen && (
          <div
            className="fixed inset-0 bg-black/50 z-40 lg:hidden"
            onClick={() => setSidebarOpen(false)}
          />
        )}

        {/* Main Content */}
        <main className="flex-1 min-h-screen">
          <div className="p-6 lg:p-8">
            {children}
          </div>
        </main>
      </div>
    </div>
  );
};
